import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripeConfig, createPaymentIntent } from '../services/api';
import { theme } from '../config/theme';
import { CreditCardIcon, CheckIcon, XIcon, XCircleIcon } from 'lucide-react';

interface StripePaymentFormProps {
  amount: number;
  customerEmail: string;
  customerName: string;
  payInFull: boolean;
  depositPercentage?: number;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentError: (error: string) => void;
}

interface CheckoutFormProps {
  amount: number;
  payInFull: boolean;
  depositPercentage?: number;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentError: (error: string) => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ amount, payInFull, depositPercentage, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/booking-success`,
      },
      redirect: 'if_required'
    });

    if (error) {
      setMessage(error.message || 'An error occurred during payment');
      onPaymentError(error.message || 'Payment failed');
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      setMessage('Payment successful!');
      onPaymentSuccess(paymentIntent.id);
      // Don't set processing to false on success - let redirect handle it
    } else {
      setMessage('Payment processing...');
      // Don't set processing to false - let redirect handle it
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={`border rounded-md p-4 mb-4 ${
        payInFull 
          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200' 
          : 'border-blue-200'
      }`}
        style={!payInFull ? { backgroundColor: '#dbeafe' } : {}}
      >
        <p className={`text-sm font-semibold ${payInFull ? 'text-blue-900' : ''}`}
          style={!payInFull ? { color: '#1e40af' } : {}}
        >
          {payInFull ? (
            <>
              <strong>Full Payment:</strong> ${amount.toFixed(2)}
            </>
          ) : (
            <>
              <strong>Deposit Payment ({depositPercentage}%):</strong> ${amount.toFixed(2)}
            </>
          )}
        </p>
        <p className={`text-xs mt-1 ${payInFull ? 'text-blue-700' : 'text-amber-700'}`}>
          {payInFull ? (
            'Complete payment today - no balance will be due later.'
          ) : (
            'This deposit is required to confirm your booking. The remaining balance will be due later.'
          )}
        </p>
      </div>

      <PaymentElement />

      {message && (
        <div className={`rounded-md p-3 ${
          message.includes('successful') 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {message.includes('successful') ? (
              <CheckIcon className="h-4 w-4 mr-2" />
            ) : (
              <XCircleIcon className="h-4 w-4 mr-2" />
            )}
            <span className="text-sm">{message}</span>
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={isProcessing || !stripe || !elements}
        className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
      >
        {isProcessing ? 'Processing...' : `Pay $${amount.toFixed(2)}${payInFull ? ' (Full Payment)' : ' (Deposit)'}`}
      </button>
    </form>
  );
};

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  amount,
  customerEmail,
  customerName,
  payInFull,
  depositPercentage = 50,
  onPaymentSuccess,
  onPaymentError
}) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let isMounted = true;
    
    const initializeStripe = async () => {
      try {
        // Fetch Stripe config using API service (includes API key)
        const config = await getStripeConfig();

        if (!isMounted) return;

        if (!config.enabled || !config.publishable_key) {
          setError('Payment processing is not available. Please contact support.');
          setLoading(false);
          return;
        }

        // Initialize Stripe only once
        if (!stripePromise) {
          setStripePromise(loadStripe(config.publishable_key));
        }

        // Create payment intent using API service (includes API key)
        const data = await createPaymentIntent({
          amount: Math.round(amount * 100), // Convert dollars to cents
          currency: 'usd',
          customer_email: customerEmail,
          customer_name: customerName,
          booking_metadata: {
            customer_name: customerName,
            customer_email: customerEmail
          }
        });

        if (!isMounted) return;
        
        setClientSecret(data.client_secret);
        setLoading(false);
      } catch (err: any) {
        if (!isMounted) return;
        
        setError(err.message || 'Failed to initialize payment');
        setLoading(false);
        onPaymentError(err.message || 'Failed to initialize payment');
      }
    };

    // Reset loading state when amount changes
    setLoading(true);
    setError('');
    initializeStripe();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [amount, customerEmail, customerName]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Initializing payment...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex items-center">
          <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!stripePromise || !clientSecret) {
    return null;
  }

  const appearance = {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#1e40af',
    },
  };

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <CheckoutForm 
        amount={amount}
        payInFull={payInFull}
        depositPercentage={depositPercentage}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
      />
    </Elements>
  );
};

export default StripePaymentForm;
